import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';

interface JobPosition {
    id: string;
    title: string;
    department: string;
    reportingTo: string;
    location: string;
    employmentType: string;
    openings: string;
    salary?: string;
    badges: string[];
    description: string;
    responsibilities: string[];
    requirements: string[];
    benefits?: string[];
    color: string;
    gradientFrom: string;
    gradientTo: string;
}

const Career: React.FC = () => {
    const [selectedJob, setSelectedJob] = useState<JobPosition | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const jobs: JobPosition[] = [
        {
            id: 'bdm',
            title: 'Business Development Manager (BDM)',
            department: 'Sales & Business Development',
            reportingTo: 'Director – Sales & Strategy',
            location: 'On-site',
            employmentType: 'Full-time',
            openings: '2 Positions',
            salary: '₹25k - ₹30k',
            badges: ['Full-time', 'On-site', '2 Positions'],
            description: 'At We4Task, we help businesses streamline operations and achieve sustainable growth through innovative solutions. We are hiring a Business Development Manager (BDM) to lead our sales efforts, drive revenue, and build long-term client relationships.',
            responsibilities: [
                'Develop and execute strategies to acquire new clients and markets',
                'Build, manage, and mentor a team of Business Development Associates',
                'Identify and pursue high-value partnerships and business opportunities',
                'Achieve and exceed sales targets and revenue goals',
                'Negotiate and close business deals with decision-makers',
                'Collaborate with marketing to design effective lead-generation campaigns',
                'Maintain and analyze sales reports, forecasts, and pipelines using CRM tools',
                'Represent We4Task at networking events, meetings, and industry conferences'
            ],
            requirements: [
                "Bachelor's degree in Business, Marketing, or related field (MBA preferred)",
                '4–8 years of proven experience in sales, business development, or client acquisition',
                'Strong leadership and team management skills',
                'Excellent negotiation, communication, and presentation abilities',
                'Proficiency with CRM tools (Zoho, Salesforce, HubSpot, etc.)',
                'Track record of meeting or exceeding sales targets'
            ],
            color: 'blue',
            gradientFrom: 'from-[#3B82F6]',
            gradientTo: 'to-blue-600'
        },
        {
            id: 'pm',
            title: 'Project Manager',
            department: 'Project Management / Operations',
            reportingTo: 'Senior Manager / Director of Operations',
            location: 'On-site',
            employmentType: 'Full-time',
            openings: '2 Positions',
            badges: ['Full-time', 'On-site', '2 Positions'],
            description: 'At We4Task, we help businesses achieve operational excellence through innovative solutions and structured project delivery. We are hiring a skilled Project Manager who can lead cross-functional teams, manage client expectations, and deliver projects successfully.',
            responsibilities: [
                'Define project scope, goals, deliverables, and timelines',
                'Create detailed project plans, schedules, and budgets',
                'Lead and coordinate cross-functional teams to ensure smooth execution',
                'Communicate project progress to stakeholders and clients',
                'Identify risks and develop mitigation strategies',
                'Monitor project performance using tools and dashboards',
                'Ensure high-quality deliverables and client satisfaction',
                'Document lessons learned and implement process improvements'
            ],
            requirements: [
                "Bachelor's degree in Business, IT, Management, or related field (MBA or PMP/Prince2 certification is a plus)",
                '3–7 years of proven experience in project management',
                'Proficiency in project management tools (Asana, Jira, Trello, MS Project, etc.)',
                'Strong leadership, communication, and team management skills',
                'Ability to manage multiple projects in a fast-paced environment',
                'Knowledge of Agile/Scrum methodologies is preferred'
            ],
            color: 'orange',
            gradientFrom: 'from-[#F59E0B]',
            gradientTo: 'to-orange-600'
        },
        {
            id: 'crm',
            title: 'CRM Executive (Entry Level)',
            department: 'Sales / Marketing / Customer Success',
            reportingTo: 'Business Development Manager',
            location: 'On-site',
            employmentType: 'Full-time',
            openings: '4 Positions',
            badges: ['Full-time', 'On-site', 'Entry Level', '4 Positions'],
            description: 'At We4Task, we focus on helping businesses simplify operations and build lasting customer relationships. We are looking for an enthusiastic CRM Executive (Entry Level) to join our team and support our customer relationship management processes. This role is ideal for freshers who are detail-oriented, tech-savvy, and eager to grow in sales and customer management.',
            responsibilities: [
                'Update and maintain customer data in CRM software (Zoho, HubSpot, Salesforce, etc.)',
                'Assist in managing leads, opportunities, and client interactions',
                'Support sales and marketing teams with CRM-related tasks',
                'Generate simple CRM reports and dashboards for internal teams',
                'Help in monitoring customer interactions and ensuring follow-ups',
                'Provide basic support to team members using the CRM system'
            ],
            requirements: [
                "Bachelor's degree in Business, Marketing, IT, or related field (freshers welcome)",
                'Basic knowledge of CRM software (Zoho, HubSpot, Salesforce, or similar) is a plus',
                'Strong attention to detail and data accuracy',
                'Good communication and organizational skills',
                'Eagerness to learn and grow in sales/CRM roles'
            ],
            color: 'green',
            gradientFrom: 'from-green-500',
            gradientTo: 'to-green-600'
        },
        {
            id: 'fme',
            title: 'Field Marketing Executive (FME)',
            department: 'Marketing & Sales',
            reportingTo: 'District/Regional Marketing Head',
            location: 'Karnataka',
            employmentType: 'Full-time',
            openings: '150 Positions',
            badges: ['Full-time', 'On-site', '150 Positions', 'Karnataka'],
            description: 'At We4Task, we help businesses grow smarter with innovative marketing and business solutions. We are looking for energetic and motivated Field Marketing Executives to join our team and represent our brand on the ground. This is a great opportunity for freshers and young professionals who want to build a career in marketing and sales.',
            responsibilities: [
                'Conduct on-ground marketing campaigns, promotions, and events',
                'Interact directly with potential customers to promote services',
                'Distribute marketing materials and generate leads',
                'Assist in customer acquisition and follow-ups',
                'Collect field data, feedback, and competitor insights',
                'Report daily/weekly activities to district or regional heads',
                'Support overall marketing strategy by providing field-level input'
            ],
            requirements: [
                'Qualification: Any Degree',
                '0–2 years of experience in sales/marketing (freshers welcome)',
                'Strong communication and interpersonal skills',
                'Confidence in interacting with customers directly',
                'Willingness to travel and work on-field',
                'Knowledge of local language is a plus'
            ],
            benefits: [
                'Fixed salary + attractive incentives',
                'Training and mentorship for career growth',
                'Opportunity to move into leadership roles (District/Regional Marketing)',
                'A dynamic, growth-focused work environment'
            ],
            color: 'pink',
            gradientFrom: 'from-pink-500',
            gradientTo: 'to-pink-600'
        },
        {
            id: 'dmh',
            title: 'District Marketing Head',
            department: 'Marketing & Sales',
            reportingTo: 'Regional/Zonal Marketing Manager',
            location: 'Karnataka',
            employmentType: 'Full-time',
            openings: '31 Positions',
            badges: ['Full-time', 'On-site', '31 Positions', 'Karnataka'],
            description: 'At We4Task, we deliver innovative marketing and business solutions to help organizations scale smarter. We are looking for a passionate and results-driven District Marketing Head to lead our marketing operations at the district level in Karnataka.',
            responsibilities: [
                'Develop and implement district-level marketing strategies',
                'Supervise and guide field marketing executives in day-to-day operations',
                'Plan and execute promotional events, campaigns, and roadshows',
                'Analyze competitor activities and market trends within the district',
                'Build strong relationships with local clients, vendors, and partners',
                'Ensure lead generation, customer engagement, and brand visibility',
                'Monitor team performance and ensure targets are achieved',
                'Report performance metrics and market insights to regional managers'
            ],
            requirements: [
                "Bachelor's degree in Marketing, Business, or related field (MBA preferred)",
                '1-2 years of experience in marketing, sales, or territory management',
                'Strong leadership, communication, and organizational skills',
                'Ability to manage field teams and coordinate multiple campaigns',
                'Knowledge of the local district market and customer behavior',
                'Proficiency in MS Office and CRM tools is an advantage'
            ],
            benefits: [
                'Competitive salary + performance-based incentives',
                'Leadership role with growth opportunities into regional/zonal management',
                'Exposure to diverse marketing campaigns and strategies',
                'Training and support for professional development'
            ],
            color: 'indigo',
            gradientFrom: 'from-indigo-500',
            gradientTo: 'to-indigo-600'
        },
        {
            id: 'rmm',
            title: 'Regional Marketing Manager',
            department: 'Marketing & Sales',
            reportingTo: 'Zonal Marketing Manager / Head of Marketing',
            location: 'Karnataka',
            employmentType: 'Full-time',
            openings: '1 Position',
            badges: ['Full-time', 'On-site', '1 Position', 'Karnataka'],
            description: "We4Task is one of India's leading multi-marketing management firms. We specialise in delivering innovative, data-driven, and customer-focused marketing strategies that empower businesses to grow. We are seeking a dynamic and experienced Regional Marketing Manager to oversee and drive our marketing operations across multiple districts in Karnataka.",
            responsibilities: [
                'Develop and implement region-wide marketing strategies and campaigns',
                'Lead, mentor, and manage district-level marketing heads and field teams',
                'Monitor performance and ensure achievement of sales and marketing targets',
                'Conduct market research to analyse customer needs, competitor activities, and emerging trends',
                'Coordinate with the Zonal Marketing Manager to align regional strategies with company objectives',
                'Plan and oversee regional events, promotions, and brand activation campaigns',
                'Optimize regional marketing budgets and resource allocation',
                'Ensure consistent brand visibility and market penetration across the region',
                'Provide detailed reports on regional performance, insights, and growth opportunities'
            ],
            requirements: [
                "Bachelor's degree in Marketing, Business Administration, or related field (MBA preferred)",
                '3-5 years of experience in marketing, sales, or territory management',
                'Proven leadership experience in managing multi-district marketing operations',
                'Strong communication, organizational, and analytical skills',
                'Ability to work under pressure and meet deadlines',
                'Proficiency in CRM, MS Office, and digital marketing tools',
                "Knowledge of Karnataka's regional markets is an advantage"
            ],
            benefits: [
                'Competitive salary with performance-based incentives',
                'Leadership role with career growth into Zonal Marketing positions',
                'Opportunity to lead large-scale marketing initiatives',
                'Professional training, mentorship, and growth opportunities'
            ],
            color: 'cyan',
            gradientFrom: 'from-cyan-500',
            gradientTo: 'to-cyan-600'
        },
        {
            id: 'zmh',
            title: 'Zonal Marketing Head',
            department: 'Marketing & Sales',
            reportingTo: 'Head of Marketing / Director',
            location: 'Across India',
            employmentType: 'Full-time',
            openings: '4 Positions',
            badges: ['Full-time', 'On-site', '4 Positions', 'Across India'],
            description: 'At We4Task, we are redefining marketing with innovative, customer-driven, and scalable strategies. As one of India\'s leading marketing management firms, we empower businesses to grow through creative campaigns, smart execution, and impactful leadership. We are seeking a visionary Zonal Marketing Head to lead our marketing operations across multiple regions in India.',
            responsibilities: [
                'Develop and execute strategic marketing plans across the assigned zone',
                'Oversee and mentor Regional Marketing Managers, District Marketing Heads, and field executives',
                'Ensure alignment of zonal strategies with overall company objectives',
                'Drive large-scale campaigns, brand activations, and promotional events',
                'Monitor and analyze zonal market trends, customer behavior, and competitor strategies',
                'Optimize marketing budgets and resource allocation for maximum ROI',
                'Ensure achievement of sales and marketing KPIs across all regions',
                'Build strong networks with clients, vendors, and stakeholders at the zonal level',
                'Provide performance reports and strategic recommendations to senior leadership'
            ],
            requirements: [
                "Bachelor's degree in Marketing, Business, or related field (MBA preferred)",
                '4-6 years of experience in marketing, sales, or business leadership',
                'Proven experience managing multi-region marketing operations',
                'Strong leadership, communication, and strategic thinking skills',
                'Ability to manage large teams and deliver under tight deadlines',
                'Proficiency in CRM, MS Office, and digital marketing tools',
                'Deep knowledge of Karnataka markets and consumer behavior is a plus'
            ],
            benefits: [
                'Competitive salary + attractive performance incentives',
                'Senior leadership position with growth into National Marketing roles',
                'Exposure to large-scale campaigns and strategic decision-making',
                'Training, mentorship, and professional development opportunities'
            ],
            color: 'violet',
            gradientFrom: 'from-violet-500',
            gradientTo: 'to-violet-600'
        }
    ];

    const openModal = (job: JobPosition) => {
        setSelectedJob(job);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedJob(null);
    };

    const scrollToOpenPositions = () => {
        const element = document.getElementById('open-positions');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const getBadgeClasses = (color: string) => {
        const colorMap: { [key: string]: string } = {
            blue: 'bg-blue-100 text-blue-700',
            orange: 'bg-orange-100 text-orange-700',
            green: 'bg-green-100 text-green-700',
            pink: 'bg-pink-100 text-pink-700',
            indigo: 'bg-indigo-100 text-indigo-700',
            cyan: 'bg-cyan-100 text-cyan-700',
            violet: 'bg-violet-100 text-violet-700'
        };
        return colorMap[color] || 'bg-gray-100 text-gray-700';
    };

    const getResponsibilityColor = (color: string) => {
        const colorMap: { [key: string]: string } = {
            blue: 'text-blue-600',
            orange: 'text-orange-600',
            green: 'text-green-600',
            pink: 'text-pink-600',
            indigo: 'text-indigo-600',
            cyan: 'text-cyan-600',
            violet: 'text-violet-600'
        };
        return colorMap[color] || 'text-gray-600';
    };

    const getResponsibilityBoxClasses = (color: string) => {
        const colorMap: { [key: string]: string } = {
            blue: 'bg-blue-50 border-blue-100',
            orange: 'bg-orange-50 border-orange-100',
            green: 'bg-green-50 border-green-100',
            pink: 'bg-pink-50 border-pink-100',
            indigo: 'bg-indigo-50 border-indigo-100',
            cyan: 'bg-cyan-50 border-cyan-100',
            violet: 'bg-violet-50 border-violet-100'
        };
        return colorMap[color] || 'bg-gray-50 border-gray-100';
    };

    return (
        <div className="min-h-screen bg-white font-sans text-gray-900">
            <PublicHeader />

            {/* Hero Section */}
            <section className="pt-32 pb-24 bg-gradient-to-br from-blue-50/40 via-white to-orange-50/30 relative overflow-hidden">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#3B82F6]/10 rounded-full blur-3xl"></div>
                    <div className="absolute top-60 -left-40 w-96 h-96 bg-[#F59E0B]/10 rounded-full blur-3xl"></div>
                </div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-5xl mx-auto text-center">
                        <div className="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-100 rounded-full mb-8">
                            <span className="w-2 h-2 bg-[#3B82F6] rounded-full mr-2 animate-pulse"></span>
                            <span className="text-sm font-medium text-[#3B82F6]">Join Our Team</span>
                        </div>
                        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-8 leading-tight">
                            <span className="text-[#3B3B3B] inline">Build Your </span>
                            <span className="text-[#3B82F6] inline bg-gradient-to-r from-[#3B82F6] to-blue-500 bg-clip-text text-transparent">Career </span>
                            <span className="text-[#3B3B3B] inline">With Us</span>
                        </h1>
                        <p className="text-xl sm:text-2xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-12">
                            Be part of something amazing. Join We4Task and transform your career with exciting opportunities and growth.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={scrollToOpenPositions}
                                className="group inline-flex items-center justify-center px-10 py-5 bg-[#3B82F6] text-white rounded-xl hover:bg-blue-700 hover:shadow-2xl transition-all duration-300 font-semibold text-base shadow-lg hover:-translate-y-1"
                            >
                                View Open Positions
                                <svg className="w-5 h-5 ml-2 group-hover:translate-y-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            <Link
                                to="/register"
                                className="group inline-flex items-center justify-center px-10 py-5 bg-white text-[#F59E0B] border-2 border-[#F59E0B] rounded-xl hover:bg-yellow-50 hover:shadow-2xl transition-all duration-300 font-semibold text-base hover:-translate-y-1"
                            >
                                Apply Now
                                <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Work With Us Section */}
            <section className="py-20 bg-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/20 via-transparent to-orange-50/20 pointer-events-none"></div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-12">
                            <div className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-6">
                                Why Choose Us
                            </div>
                            <h2 className="text-2xl md:text-3xl font-bold text-[#3B3B3B] mb-6">Why Work With Us?</h2>
                            <p className="text-xl text-gray-600 max-w-2xl mx-auto">Join a team that values growth, innovation, and your success</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            <div className="group bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl hover:border-blue-300 transition-all duration-300 hover:-translate-y-2">
                                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-[#3B3B3B] mb-4">Growth Opportunities</h3>
                                <p className="text-base text-gray-700 leading-relaxed">
                                    Accelerate your career with continuous learning and development programs designed to help you reach your full potential.
                                </p>
                            </div>

                            <div className="group bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl hover:border-purple-300 transition-all duration-300 hover:-translate-y-2">
                                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-[#3B3B3B] mb-4">Collaborative Culture</h3>
                                <p className="text-base text-gray-700 leading-relaxed">
                                    Work with passionate professionals in a supportive and inclusive environment that fosters creativity and teamwork.
                                </p>
                            </div>

                            <div className="group bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl hover:border-green-300 transition-all duration-300 hover:-translate-y-2">
                                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-[#3B3B3B] mb-4">Work-Life Balance</h3>
                                <p className="text-base text-gray-700 leading-relaxed">
                                    Enjoy flexible working arrangements and comprehensive benefits package that supports your well-being.
                                </p>
                            </div>

                            <div className="group bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl hover:border-orange-300 transition-all duration-300 hover:-translate-y-2">
                                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-[#3B3B3B] mb-4">Innovation Focus</h3>
                                <p className="text-base text-gray-700 leading-relaxed">
                                    Be at the forefront of marketing innovation and cutting-edge strategies that shape the industry.
                                </p>
                            </div>

                            <div className="group bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl hover:border-cyan-300 transition-all duration-300 hover:-translate-y-2">
                                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-[#3B3B3B] mb-4">Recognition & Rewards</h3>
                                <p className="text-base text-gray-700 leading-relaxed">
                                    Your contributions are valued and rewarded with competitive compensation and recognition programs.
                                </p>
                            </div>

                            <div className="group bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl hover:border-indigo-300 transition-all duration-300 hover:-translate-y-2">
                                <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-[#3B3B3B] mb-4">Global Impact</h3>
                                <p className="text-base text-gray-700 leading-relaxed">
                                    Work on projects that make a difference across various industries and markets worldwide.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Open Positions Section */}
            <section id="open-positions" className="py-20 bg-gradient-to-b from-gray-50 to-white relative">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-12">
                            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
                                Open Positions
                            </div>
                            <h2 className="text-2xl md:text-3xl font-bold text-[#3B3B3B] mb-6">Open Positions</h2>
                            <p className="text-xl text-gray-600 max-w-2xl mx-auto">Find your perfect role and start your journey with us</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {jobs.map((job) => (
                                <div key={job.id} className="group bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 flex flex-col">
                                    <div className="flex-1">
                                        <h3 className="text-lg font-bold text-[#3B3B3B] mb-3">{job.title}</h3>
                                        <div className="flex flex-wrap gap-2 mb-4">
                                            {job.badges.map((badge, idx) => (
                                                <span key={idx} className={`px-3 py-1 ${getBadgeClasses(job.color)} rounded-full text-xs font-medium`}>
                                                    {badge}
                                                </span>
                                            ))}
                                        </div>
                                        <p className="text-base text-gray-600 mb-4 line-clamp-3">{job.description}</p>
                                        <div className="space-y-2 mb-4">
                                            <div className="flex items-center text-sm text-gray-600">
                                                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                {job.location}
                                            </div>
                                            {job.salary && (
                                                <div className="flex items-center text-sm font-semibold text-[#3B3B3B]">
                                                    <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    {job.salary}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-3 pt-4 border-t border-gray-200">
                                        <button
                                            onClick={() => openModal(job)}
                                            className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
                                        >
                                            View Details
                                        </button>
                                        <Link
                                            to="/register"
                                            className={`flex-1 px-4 py-2 bg-gradient-to-r ${job.gradientFrom} ${job.gradientTo} text-white rounded-lg hover:shadow-lg transition-all font-medium text-sm text-center`}
                                        >
                                            Apply Now
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Job Details Modal */}
            {isModalOpen && selectedJob && (
                <div className="fixed inset-0 z-50 overflow-y-auto" onClick={closeModal}>
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={closeModal}></div>
                        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" onClick={(e) => e.stopPropagation()}>
                            <div className="bg-white px-6 pt-6 pb-4 sm:p-8">
                                <div className="flex items-start justify-between mb-6">
                                    <div className="flex-1">
                                        <h3 className="text-xl font-bold text-[#3B3B3B] mb-4">{selectedJob.title}</h3>
                                        <div className="flex flex-wrap gap-3 mb-6">
                                            {selectedJob.badges.map((badge, idx) => (
                                                <span key={idx} className={`px-4 py-2 ${getBadgeClasses(selectedJob.color)} rounded-full text-sm font-semibold`}>
                                                    {badge}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <button
                                        onClick={closeModal}
                                        className="ml-4 text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <p className="text-base text-gray-700 leading-relaxed mb-8">{selectedJob.description}</p>

                                <div className="mb-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                        <div className="bg-gray-50 p-4 rounded-lg">
                                            <p className="text-sm text-gray-600 mb-1">Department</p>
                                            <p className="font-semibold text-[#3B3B3B]">{selectedJob.department}</p>
                                        </div>
                                        <div className="bg-gray-50 p-4 rounded-lg">
                                            <p className="text-sm text-gray-600 mb-1">Reporting To</p>
                                            <p className="font-semibold text-[#3B3B3B]">{selectedJob.reportingTo}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className={`mb-8 ${getResponsibilityBoxClasses(selectedJob.color)} p-6 rounded-xl border`}>
                                    <h4 className="text-lg font-bold text-[#3B3B3B] mb-4">Key Responsibilities:</h4>
                                    <ul className="space-y-3 text-base text-gray-700">
                                        {selectedJob.responsibilities.map((resp, idx) => (
                                            <li key={idx} className="flex items-start">
                                                <span className={`${getResponsibilityColor(selectedJob.color)} mr-3 mt-1`}>•</span>
                                                <span>{resp}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="mb-8 bg-green-50 p-6 rounded-xl border border-green-100">
                                    <h4 className="text-lg font-bold text-[#3B3B3B] mb-4">Requirements:</h4>
                                    <ul className="space-y-3 text-base text-gray-700">
                                        {selectedJob.requirements.map((req, idx) => (
                                            <li key={idx} className="flex items-start">
                                                <span className="text-green-600 mr-3 mt-1">✓</span>
                                                <span>{req}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {selectedJob.benefits && selectedJob.benefits.length > 0 && (
                                    <div className="mb-8 bg-purple-50 p-6 rounded-xl border border-purple-100">
                                        <h4 className="text-lg font-bold text-[#3B3B3B] mb-4">What We Offer:</h4>
                                        <ul className="space-y-3 text-base text-gray-700">
                                            {selectedJob.benefits.map((benefit, idx) => (
                                                <li key={idx} className="flex items-start">
                                                    <span className="text-purple-600 mr-3 mt-1">•</span>
                                                    <span>{benefit}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {selectedJob.salary && (
                                    <div className="mb-6 pt-6 border-t border-gray-200">
                                        <p className="text-sm text-gray-600 mb-1">Salary Range</p>
                                        <p className="text-2xl font-bold text-[#3B3B3B]">{selectedJob.salary}</p>
                                    </div>
                                )}

                                <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
                                    <button
                                        onClick={closeModal}
                                        className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-semibold"
                                    >
                                        Close
                                    </button>
                                    <Link
                                        to="/register"
                                        onClick={closeModal}
                                        className={`flex-1 px-6 py-3 bg-gradient-to-r ${selectedJob.gradientFrom} ${selectedJob.gradientTo} text-white rounded-xl hover:shadow-2xl transition-all font-semibold text-center`}
                                    >
                                        Apply Now
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Application Process Section */}
            <section className="py-20 bg-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/20 via-transparent to-orange-50/20 pointer-events-none"></div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-12">
                            <div className="inline-block px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold mb-6">
                                How It Works
                            </div>
                            <h2 className="text-2xl md:text-3xl font-bold text-[#3B3B3B] mb-6">Application Process</h2>
                            <p className="text-xl text-gray-600 max-w-2xl mx-auto">Simple steps to join our team</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            <div className="group text-center bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl hover:border-blue-300 transition-all duration-300 hover:-translate-y-2">
                                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform">
                                    <span className="text-3xl font-bold text-white">1</span>
                                </div>
                                <h3 className="text-lg font-bold text-[#3B3B3B] mb-3">Apply Online</h3>
                                <p className="text-base text-gray-700 leading-relaxed">Submit your application through our website</p>
                            </div>

                            <div className="group text-center bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl hover:border-orange-300 transition-all duration-300 hover:-translate-y-2">
                                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform">
                                    <span className="text-3xl font-bold text-white">2</span>
                                </div>
                                <h3 className="text-lg font-bold text-[#3B3B3B] mb-3">Initial Review</h3>
                                <p className="text-base text-gray-700 leading-relaxed">Our team reviews your qualifications</p>
                            </div>

                            <div className="group text-center bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl hover:border-green-300 transition-all duration-300 hover:-translate-y-2">
                                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform">
                                    <span className="text-3xl font-bold text-white">3</span>
                                </div>
                                <h3 className="text-lg font-bold text-[#3B3B3B] mb-3">Interview</h3>
                                <p className="text-base text-gray-700 leading-relaxed">Meet with our hiring team</p>
                            </div>

                            <div className="group text-center bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl hover:border-purple-300 transition-all duration-300 hover:-translate-y-2">
                                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform">
                                    <span className="text-3xl font-bold text-white">4</span>
                                </div>
                                <h3 className="text-lg font-bold text-[#3B3B3B] mb-3">Welcome Aboard</h3>
                                <p className="text-base text-gray-700 leading-relaxed">Join the We4Task family</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-[#3B82F6] via-blue-600 to-[#F59E0B] relative overflow-hidden">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 leading-tight">
                            Ready to Start Your Career Journey?
                        </h2>
                        <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
                            Take the first step towards an exciting career with We4Task. Join us and be part of something amazing.
                        </p>
                        <Link
                            to="/register"
                            className="group inline-flex items-center justify-center px-10 py-5 bg-white text-[#3B82F6] rounded-xl hover:bg-gray-50 hover:shadow-2xl transition-all duration-300 font-semibold text-base hover:-translate-y-1"
                        >
                            Apply Today
                            <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </Link>
                    </div>
                </div>
            </section>

            <PublicFooter />
        </div>
    );
};

export default Career;
